<template>
	<div class="trust" id="trust">

		<!-- 01 Banking partners -->
		<section class="trust-section tone-white" aria-labelledby="trust-banks">
			<div class="trust-head" data-reveal>
				<div><PdEyebrow>01 / Banking partners</PdEyebrow><h3 id="trust-banks">Your money moves through banks you know.</h3></div>
				<p>Paydeck works with established Indian banks. Your payment travels over regulated banking channels, from your bank to the recipient's account, with a record at every step.</p>
			</div>
			<div class="trust-bento" data-reveal>
				<PdTile tone="ink" pad="lg" rings class="fact-tile span-4 rows-2">
					<PdEyebrow tone="light">In the network</PdEyebrow>
					<strong class="fact-value pd-num">{{ banks.length }}</strong>
					<div>
						<p class="fact-label">Indian banks in the Paydeck network</p>
						<p class="fact-note">Each one is licensed and supervised by the Reserve Bank of India.</p>
					</div>
				</PdTile>
				<div v-for="item in banks" :key="item.name" class="logo-tile span-4"><PartnerLogo :name="item.name" :src="item.src" :logo-style="item.style" /></div>
			</div>
		</section>

		<!-- 02 Card networks -->
		<section class="trust-section tone-cream" aria-labelledby="trust-cards">
			<div class="trust-head" data-reveal>
				<div><PdEyebrow>02 / Card networks</PdEyebrow><h3 id="trust-cards">Pay with the card already in your wallet.</h3></div>
				<p>Use a credit card on the networks India relies on every day. No new card, and no new account with a bank you have never heard of.</p>
			</div>
			<div class="trust-bento" data-reveal>
				<div v-for="item in cardNetworks" :key="item.name" class="logo-tile logo-tile-tall span-4"><PartnerLogo :name="item.name" :src="item.src" :logo-style="item.style" /></div>
				<PdTile tone="white" pad="lg" class="span-8">
					<PdEyebrow tone="muted">How a card payment is approved</PdEyebrow>
					<ol class="approve-steps">
						<li><b class="pd-num">1</b><span>You review the amount, the purpose and the recipient.</span></li>
						<li><b class="pd-num">2</b><span>Your bank checks it is really you, usually with an OTP.</span></li>
						<li><b class="pd-num">3</b><span>You get a confirmation and a reference to keep.</span></li>
					</ol>
				</PdTile>
				<PdTile tone="orange" pad="lg" rings class="fact-tile span-4">
					<ShieldCheck :size="34" :stroke-width="1.8" aria-hidden="true" />
					<div>
						<p class="fact-title">Approved by your bank, not by us.</p>
						<p class="fact-note">Your card issuer authenticates every payment before it goes through.</p>
					</div>
				</PdTile>
			</div>
		</section>

		<!-- 03 Payment gateways -->
		<section class="trust-section tone-white" aria-labelledby="trust-gateways">
			<div class="trust-head" data-reveal>
				<div><PdEyebrow>03 / Payment gateway partners</PdEyebrow><h3 id="trust-gateways">Processed by gateways India already trusts.</h3></div>
				<p>Card payments on Paydeck are processed by leading Indian payment gateways, the same partners behind the checkouts of thousands of businesses.</p>
			</div>
			<div class="trust-bento" data-reveal>
				<PdTile tone="ink" pad="lg" class="fact-tile span-4 rows-3">
					<span class="fact-icon"><Lock :size="26" :stroke-width="2" aria-hidden="true" /></span>
					<div>
						<p class="fact-title">Your card details stay with the gateway.</p>
						<p class="fact-note">Paydeck does not store your complete card details. Card information is handled by secure banking and payment partners.</p>
					</div>
					<p class="fact-foot"><strong class="pd-num">{{ gateways.length }}</strong> gateway partners</p>
				</PdTile>
				<div v-for="item in gateways" :key="item.name" class="logo-tile span-4"><PartnerLogo :name="item.name" :src="item.src" :logo-style="item.style" /></div>
			</div>
		</section>

		<!-- 04 Registered entity -->
		<section class="trust-section tone-cream" aria-labelledby="trust-entity">
			<div class="trust-head" data-reveal>
				<div><PdEyebrow>04 / Registered entity</PdEyebrow><h3 id="trust-entity">A registered Indian company. Accountable by law.</h3></div>
				<p>Paydeck is incorporated in India and registered with the Ministry of Corporate Affairs. Our identity, address and policies are public, so you always know who you are dealing with.</p>
			</div>
			<div class="trust-bento" data-reveal>
				<PdTile tone="white" pad="lg" class="mca-tile span-5 rows-2">
					<PartnerLogo :name="mca.name" :src="mca.src" :logo-style="mca.style" />
					<p class="mca-caption">Registered with the Ministry of Corporate Affairs, Government of India</p>
				</PdTile>
				<PdTile tone="white" pad="lg" class="span-7">
					<PdEyebrow tone="muted">Company details</PdEyebrow>
					<dl class="company-details">
						<div><dt>Legal name</dt><dd>{{ company.name }}</dd></div>
						<div><dt>CIN</dt><dd class="pd-num">{{ company.cin }}</dd></div>
						<div><dt>Registered office</dt><dd>{{ company.office }}</dd></div>
					</dl>
				</PdTile>
				<PdTile tone="ink" pad="lg" class="fact-tile span-3">
					<PdEyebrow tone="light">Since</PdEyebrow>
					<div>
						<strong class="fact-value fact-value-sm pd-num">2014</strong>
						<p class="fact-label">Making everyday payments possible</p>
					</div>
				</PdTile>
				<PdTile tone="orange" pad="lg" class="fact-tile span-4">
					<p class="fact-title">Our policies, in plain view.</p>
					<div class="policy-links">
						<PdArrowLink v-for="link in policyLinks" :key="link.href" :href="link.href" tone="light">{{ link.label }}</PdArrowLink>
					</div>
				</PdTile>
			</div>
		</section>

		<!-- 05 Media -->
		<section class="trust-section tone-white trust-press" aria-labelledby="trust-media">
			<div class="trust-head" data-reveal>
				<div><PdEyebrow>05 / In the press</PdEyebrow><h3 id="trust-media">Covered by India's startup press.</h3></div>
				<p>Independent publications have reported on the Paydeck journey since 2016. Read what they wrote, in their own words.</p>
			</div>
			<PressCarousel />
		</section>
	</div>
</template>

<script setup>
import { Lock, ShieldCheck } from "lucide-vue-next";
import PdTile from "@paydeck-ui/components/PdTile.vue";
import PdEyebrow from "@paydeck-ui/components/PdEyebrow.vue";
import PdArrowLink from "@paydeck-ui/components/PdArrowLink.vue";
import PartnerLogo from "./PartnerLogo.vue";
import PressCarousel from "./PressCarousel.vue";
import { banks, cardNetworks, gateways, mca, company } from "@/content/trust";

const policyLinks = [
	{ label: "Terms of Service", href: "/terms-of-service" },
	{ label: "Privacy Policy", href: "/privacy-policy" },
	{ label: "Refund Policy", href: "/refund-policy" },
	{ label: "Fraud reporting", href: "/settlement-guide" }
];
</script>
